// The review app shows each page in a frame, as the site serves it, scripts
// and all (see "Translation review" in CLAUDE.md). It cuts the html it
// fetched into segments here (annotate.ts), and the page itself, the moment
// main.js starts and before any other script has moved or added a node,
// lists its nodes (_javascript/src/review-frame/snapshot.js). Both lists hold
// every node in document order as a token made by the functions below, so
// the app can line them up (align) and tell the page which of its nodes are
// which segment's.
//
// A token says what a node is: an element's name, id and class (as the html
// has them: the snapshot is taken before scripts change any), a hash of a
// text node's text, or that it is a comment or the doctype.

export const COMMENT_TOKEN = "!";
export const DOCTYPE_TOKEN = "!doctype";

export function elementToken(name: string, id: string | null, cls: string | null): string {
  return name + (id ? `#${id}` : "") + (cls ? `.${cls}` : "");
}

// FNV-1a over the text's UTF-16 code units.
export function textToken(data: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < data.length; i++) {
    hash ^= data.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `#${(hash >>> 0).toString(36)}`;
}

// The same token with the text left out: the English page and its copy have
// the same nodes with different words in them.
export const structureOf = (token: string): string => (token.startsWith("#") ? "#" : token);

// Myers' diff: for each item of `a`, the index of the equal item of `b` it was
// paired with, or -1. Null when more than `limit` items would have to be added
// or taken out, which is not the same page.
export function align(a: readonly string[], b: readonly string[], limit = 1000): Int32Array | null {
  const map = new Int32Array(a.length).fill(-1);
  // The usual case: nothing differs, or a little in the middle.
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) {
    map[start] = start;
    start++;
  }
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
    map[endA] = endB;
  }
  const n = endA - start;
  const m = endB - start;
  if (n === 0 || m === 0) return Math.max(n, m) > limit ? null : map;

  // v[k] is the furthest x reached on diagonal k (x - y = k); trace[d] is v
  // as it was before step d, kept for diagonals -d..d only.
  const max = Math.min(n + m, limit);
  const offset = max + 1;
  const v = new Int32Array(2 * max + 3);
  const trace: Int32Array[] = [];
  for (let d = 0; d <= max; d++) {
    trace.push(v.slice(offset - d - 1, offset + d + 2));
    for (let k = -d; k <= d; k += 2) {
      let x = k === -d || (k !== d && v[offset + k - 1] < v[offset + k + 1]) ? v[offset + k + 1] : v[offset + k - 1] + 1;
      let y = x - k;
      while (x < n && y < m && a[start + x] === b[start + y]) {
        x++;
        y++;
      }
      v[offset + k] = x;
      // (A path that ran off the end, x > n or y > m, costs more than one
      // that didn't and never comes back to the corner.)
      if (x === n && y === m) {
        backtrack(trace, n, m, (i, j) => (map[start + i] = start + j));
        return map;
      }
    }
  }
  return null;
}

function backtrack(trace: Int32Array[], n: number, m: number, pair: (i: number, j: number) => void): void {
  let x = n;
  let y = m;
  for (let d = trace.length - 1; d >= 0; d--) {
    // trace[d] holds diagonals -d-1..d+1, so diagonal k is at k + d + 1.
    const v = trace[d];
    const at = (k: number) => v[k + d + 1];
    const k = x - y;
    const prevK = k === -d || (k !== d && at(k - 1) < at(k + 1)) ? k + 1 : k - 1;
    const prevX = at(prevK);
    const prevY = prevX - prevK;
    while (x > prevX && y > prevY) {
      x--;
      y--;
      pair(x, y);
    }
    x = prevX;
    y = prevY;
  }
}

// From a map of a's items to b's, the map back.
export function invert(map: Int32Array, length: number): Int32Array {
  const back = new Int32Array(length).fill(-1);
  map.forEach((j, i) => {
    if (j >= 0) back[j] = i;
  });
  return back;
}
